// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Equal Earth projection, for every flat world map in the app
 * (owner, 2026-10-03: never Mercator anywhere; Equal Earth instead).
 *
 * Equal Earth (Šavrič, Patterson & Jenny, 2018) is an equal-area
 * pseudocylindrical projection: areas keep their true proportions, the
 * parallels are straight horizontal lines, and the meridians are curves
 * symmetric about the central one. This is the published formula with
 * its Newton inverse; no library.
 *
 * Coordinates: longitude and latitude in degrees; projected x and y in
 * the formula's own units, x east and y north, the world spanning
 * ±{@link EE_X_MAX} by ±{@link EE_Y_MAX}.
 */

const A1 = 1.340264
const A2 = -0.081106
const A3 = 0.000893
const A4 = 0.003796
const M = Math.sqrt(3) / 2
const RAD = Math.PI / 180

/** Polynomial in θ for y, and its derivative (which also scales x). */
const poly = (t2: number, t6: number) => A1 + A2 * t2 + t6 * (A3 + A4 * t2)
const dpoly = (t2: number, t6: number) => A1 + 3 * A2 * t2 + t6 * (7 * A3 + 9 * A4 * t2)

/** Project a point: [x, y], y north. */
export function equalEarth(lon: number, lat: number): [number, number] {
  const theta = Math.asin(M * Math.sin(lat * RAD))
  const t2 = theta * theta
  const t6 = t2 * t2 * t2
  const x = (lon * RAD * Math.cos(theta)) / (M * dpoly(t2, t6))
  const y = theta * poly(t2, t6)
  return [x, y]
}

/** Half the projected width of the world (at the equator). */
export const EE_X_MAX = equalEarth(180, 0)[0]
/** Half the projected height of the world (at the poles). */
export const EE_Y_MAX = equalEarth(0, 90)[1]

/** The parametric latitude θ for a projected y (Newton's method). */
function thetaOfY(y: number): number {
  let t = y
  for (let i = 0; i < 12; i++) {
    const t2 = t * t
    const t6 = t2 * t2 * t2
    const delta = (t * poly(t2, t6) - y) / dpoly(t2, t6)
    t -= delta
    if (Math.abs(delta) < 1e-12) break
  }
  return t
}

/** The latitude of a projected y, and the factor turning x into longitude on that row (x · factor = lon). */
export function equalEarthRow(y: number): { lat: number; lonPerX: number } | null {
  if (Math.abs(y) > EE_Y_MAX + 1e-9) return null
  const t = thetaOfY(y)
  const s = Math.sin(t) / M
  if (Math.abs(s) > 1) return null
  const t2 = t * t
  const t6 = t2 * t2 * t2
  return { lat: Math.asin(s) / RAD, lonPerX: (M * dpoly(t2, t6)) / Math.cos(t) / RAD }
}

/** Unproject a point: [lon, lat], or null outside the world's outline. */
export function equalEarthInvert(x: number, y: number): [number, number] | null {
  const row = equalEarthRow(y)
  if (!row) return null
  const lon = x * row.lonPerX
  if (!Number.isFinite(lon) || Math.abs(lon) > 180 + 1e-9) return null
  return [lon, row.lat]
}
